import { createSelector } from "@reduxjs/toolkit";
import { RewardsAccount } from "redmond";
import { IStoreState } from "../../../../reducers/types";

export const getIsFirstLaunch = (state: IStoreState) =>
  state.rewards.isFirstLaunch;

export const getAgentEmail = (state: IStoreState) => state.rewards.agentEmail;

export const getRewardsAccounts = (state: IStoreState) =>
  state.rewards.rewardsAccounts;

export const getUserSelectedAccountReferenceId = (state: IStoreState) =>
  state.rewards.selectedAccountReferenceId;

export const getFetchRewardsAccountsCallState = (state: IStoreState) =>
  state.rewards.fetchRewardsAccountsCallState;

export const getSortedRewardsAccounts = createSelector(
  getRewardsAccounts,
  (rewardsAccounts): RewardsAccount[] => {
    const earnMultiplier = (account: RewardsAccount) =>
      account.earn.hotelsMultiplier ?? -1;
    return rewardsAccounts.sort((prev, current) => {
      return earnMultiplier(current) - earnMultiplier(prev);
    });
  }
);

export const getRewardsAccountWithLargestValue = createSelector(
  getRewardsAccounts,
  (rewardsAccounts): RewardsAccount | undefined => {
    const sortedAccts = [...rewardsAccounts].sort((prev, current) => {
      // sorting [...rewardsAccounts] so it does not mutate the original rewardsAccounts array as that was sorted in getSortedRewardsAccounts by earn
      return (
        current.rewardsCashEquivalent.value - prev.rewardsCashEquivalent.value
      );
    });
    return sortedAccts[0];
  }
);

export const getRewardsAccountWithLargestEarnForBanner = createSelector(
  getRewardsAccounts,
  (rewardsAccounts): RewardsAccount => rewardsAccounts[0]
);

// the rewards account selection doesn't have a default state where none of the available options is selected
export const getDefaultSelectedAccountReferenceId = createSelector(
  getRewardsAccounts,
  (rewardsAccounts): string | null => {
    // return the id with largest value
    if (rewardsAccounts.length > 0) {
      return rewardsAccounts[0].accountReferenceId;
    }

    return null;
  }
);

export const getDefaultSelectedAccountReferenceIdIfRedemptionEnabled =
  createSelector(getSortedRewardsAccounts, (rewardsAccounts): string | null => {
    const filteredAccounts = rewardsAccounts.filter(
      (account) => account.allowRewardsRedemption ?? true
    );

    if (filteredAccounts.length > 0) {
      return filteredAccounts[0].accountReferenceId;
    }

    return null;
  });

// this is the primary selector used outside the component
// either returns the user selected account id, or the default from above if not present
export const getSelectedAccountReferenceId = createSelector(
  getUserSelectedAccountReferenceId,
  getDefaultSelectedAccountReferenceId,
  (
    userSelectedAccountReferenceId,
    defaultSelectedAccountReferenceId
  ): string | null => {
    return userSelectedAccountReferenceId ?? defaultSelectedAccountReferenceId;
  }
);

export const getSelectedAccount = createSelector(
  getRewardsAccounts,
  getSelectedAccountReferenceId,
  (rewardsAccounts, referenceId): RewardsAccount | undefined => {
    return rewardsAccounts.find(
      (account) => account.accountReferenceId === referenceId
    );
  }
);

export const getSelectedAccountIndex = createSelector(
  getRewardsAccounts,
  getSelectedAccountReferenceId,
  (accounts, id) => {
    return accounts.findIndex((account) => account.accountReferenceId === id);
  }
);
